class Signal {
  private _resolve: (() => void) | null;
  private _promise: Promise<void>;
  private _ready: boolean;

  static timeout(delay: number): Signal {
    const s = new Signal();
    setTimeout(() => s.broadcast(), delay);
    return s;
  }

  constructor() {
    this._resolve = null;
    this._promise = new Promise<void>((r) => {
      this._resolve = () => {
        this._ready = true;
        r();
      };
    });
    this._ready = false;
  }

  broadcast() {
    this._resolve?.();
  }

  ready(): boolean {
    return this._ready;
  }

  wait(): Promise<void> {
    return this._promise;
  }
}

class PeriodicTask<T> {
  private _interval: number;
  private _cancel: Signal;
  private _ready: Signal;
  private _callback: (() => Promise<T>) | null;

  constructor(interval: number) {
    this._interval = interval;
    this._cancel = new Signal();
    this._ready = new Signal();
    this._callback = null;

    this.loop();
  }

  private async loop(): Promise<void> {
    for (;;) {
      // if we've cancelled, exit now
      if (this._cancel.ready()) {
        return;
      }

      // create a new ready signal, if it gets triggered while we're
      // executing, we won't delay at all next time around
      this._ready = new Signal();
      // if the callback has been set, run it
      if (this._callback) {
        const p = this._callback();
        await Promise.race([this._cancel.wait(), p]);
      }

      // if ready wasn't triggered, wait the interval
      if (!this._ready.ready()) {
        this._ready = Signal.timeout(this._interval);
        await Promise.race([this._cancel.wait(), this._ready.wait()]);
      }
    }
  }

  cancel(): void {
    this._cancel.broadcast();
  }

  run(f: () => Promise<T>): void {
    this._callback = f;
    this._ready.broadcast();
  }
}

export default PeriodicTask;
